export type NoticeChannel = 'text' | 'email' | 'popup' | 'none';

/** Who can see this preference. 'all' = everyone; 'manager_or_admin' = only managers/admins; 'has_locations' = only users with at least one assigned location. */
export type NoticeRequiredAccess = 'all' | 'manager_or_admin' | 'has_locations';

export interface NoticePlaceholder {
  code: string;
  label: string;
  description?: string;
  defaultChannels?: NoticeChannel[];
  /** If set, this preference is only shown when the user has the required access. */
  requiredAccess?: NoticeRequiredAccess;
  /** If true, this is a location-wide notification (user subscribes per location). Shown only when user has locations. */
  locationScoped?: boolean;
}

export const NOTICE_REMINDER_DAYS_OPTIONS = [
  { value: '', label: 'No reminder' },
  { value: '1', label: 'After 1 day' },
  { value: '2', label: 'After 2 days' },
  { value: '3', label: 'After 3 days' },
  { value: '5', label: 'After 5 days' },
  { value: '7', label: 'After 7 days' },
  { value: '14', label: 'After 14 days' },
] as const;

export const NOTICE_PLACEHOLDERS: NoticePlaceholder[] = [
  {
    code: 'event-updates',
    label: 'Event Updates',
    description: 'Changes to event timelines, rooms, or client requests.',
    defaultChannels: ['email'],
    requiredAccess: 'all',
  },
  {
    code: 'service-ticket-updates',
    label: 'Service Ticket Updates',
    description: 'Updates for service tickets you follow directly or through a location, venue, or customer.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'transfer-order-updates',
    label: 'Transfer Order Updates',
    description: 'New and updated inbound or outbound transfer orders covered by your subscriptions.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'purchase-order-updates',
    label: 'Purchase Order Updates',
    description: 'New and updated purchase orders covered by your subscriptions.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'cross-rental-updates',
    label: 'Vendor Rental Updates',
    description: 'New and updated vendor rental inventory covered by your subscriptions.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'inventory-alerts',
    label: 'Inventory Alerts',
    description: 'Warnings when preferred gear is low or unavailable at a site.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
  },
  {
    code: 'task-assignments',
    label: 'Task Assignments',
    description: 'New tasks or checklist updates assigned to the user.',
    defaultChannels: ['email'],
    requiredAccess: 'all',
  },
  {
    code: 'task-completed',
    label: 'Task Completed',
    description: 'When a task you assigned is completed by a team member.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'manager_or_admin',
  },
  {
    code: 'approvals',
    label: 'Approval Requests',
    description: 'Notifications when your approval is needed on a quote or order.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'manager_or_admin',
  },
  // Location-wide: orders at the user's location(s)
  {
    code: 'location-order-created',
    label: 'New order at my location',
    description: 'When a new order is created for one of your locations.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
    locationScoped: true,
  },
  {
    code: 'location-order-reserved',
    label: 'Order reserved at my location',
    description: 'When an order is reserved at one of your locations.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
    locationScoped: true,
  },
  {
    code: 'location-order-ready-to-invoice',
    label: 'Order ready to invoice at my location',
    description: 'When an order at one of your locations is ready to invoice.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
    locationScoped: true,
  },
  // Location-wide: transfer orders
  {
    code: 'location-transfer-new',
    label: 'New transfer order for my location',
    description: 'When a new transfer order is created involving one of your locations (inbound or outbound).',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
    locationScoped: true,
  },
  {
    code: 'location-transfer-time-to-pick',
    label: 'Transfer ready to pick / receive at my location',
    description: 'When it’s time to pick (outbound) or receive (inbound) a transfer at one of your locations.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'has_locations',
    locationScoped: true,
  },
  // Invoice workflow notifications
  {
    code: 'invoice-review',
    label: 'Invoice Review Requested',
    description: 'When you are asked to review and approve an event for invoicing.',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'event-invoiced',
    label: 'Event Invoiced',
    description: 'When an event has been invoiced (accounting notification).',
    defaultChannels: ['email', 'popup'],
    requiredAccess: 'all',
  },
  // Post-event questionnaires (2026-10-22): the notice_code values private.post_event_message writes.
  // docs/post-event-questionnaires.md.
  {
    code: 'post_event_reminders',
    label: 'Post-event reminders',
    description: "Install check-in, photo reminders and the report link on events you're scheduled on.",
    defaultChannels: ['email', 'text'],
    requiredAccess: 'all',
  },
  {
    code: 'post_event_sign',
    label: 'Crew report to sign',
    description: "When you're an event's project manager and its crew report needs your signature.",
    defaultChannels: ['email', 'text', 'popup'],
    requiredAccess: 'all',
  },
  {
    code: 'post_event_low_rating',
    label: 'Low customer rating',
    description: "When a customer rates an event you sold or manage at or below the location's alert level.",
    defaultChannels: ['email', 'text', 'popup'],
    requiredAccess: 'all',
  },
];

export const NOTICE_CHANNEL_OPTIONS: { value: NoticeChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text' },
  { value: 'popup', label: 'In-App Popup' },
  { value: 'none', label: 'None' },
];

export type NoticeVisibilityContext = {
  isManager: boolean;
  isAdmin: boolean;
  hasLocations: boolean;
};

/** Returns only notice types the user has access to. Use for Communication preferences UI. */
export function getVisibleNoticePlaceholders(
  context: NoticeVisibilityContext,
): NoticePlaceholder[] {
  return NOTICE_PLACEHOLDERS.filter((p) => {
    const access = p.requiredAccess ?? 'all';
    if (access === 'all') return true;
    if (access === 'manager_or_admin') return context.isManager || context.isAdmin;
    if (access === 'has_locations') return context.hasLocations;
    return true;
  });
}
